import { TransformFunction } from '../../interfaces/data-mapper/transform-function';
import { IScriptBase } from '../../interfaces/script/script-base';
import { Guid } from '../guid';
import { Mapping } from './mapping';

/**
 * A single transformation rule within a DataMapper task. Reads from one or more source
 * `Mapping` paths, optionally applies a `transformFunctions` chain or a custom JavaScript
 * script, and writes the result to a single `destinationMapping` path.
 */
export class Transformation implements IScriptBase {
	/** Unique identifier for this transformation rule. */
	id = Guid();

	/** Zero-based execution order. Must be unique and sequential across all transformations (0, 1, 2, …). */
	index = 0;

	/** One or more source field paths to resolve. Multiple sources are combined before being written to the destination. */
	sourceMappings: Mapping[] = [];

	/** The output field path where the transformed result is written. */
	destinationMapping?: Mapping;

	/**
	 * When `true`, the task fails if any source value is missing or null.
	 * When `false`, missing source values are silently skipped.
	 * @default true
	 */
	required = true;

	/**
	 * Optional chain of built-in transform functions (string, math, datetime, object operations)
	 * applied left-to-right to the resolved source value. Mutually exclusive with `scriptJSBase64`.
	 */
	transformFunctions?: TransformFunction[] = [];

	/**
	 * Base64-encoded JavaScript for fully custom transformation logic.
	 * When set, `transformFunctions` is ignored. The resolved values from `sourceMappings` are
	 * passed as named function parameters (using the last `objectKey` from each source mapping
	 * path). The function's return value is written to `destinationMapping`.
	 * Mutually exclusive with `transformFunctions`.
	 */
	scriptJSBase64?: string;

	/**
	 * Base64-encoded TypeScript source for UI display only. Not executed by the backend.
	 * Typically stored alongside `scriptJSBase64` so users can view and edit the readable source.
	 */
	scriptTSBase64?: string;

	constructor(init?: Partial<Transformation>) {
		if (init) {
			Object.assign(this, init);
		}
	}
}
